import React, { useEffect, useState, useCallback } from "react";
import { api, formatDuration, formatApiError, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Timer, Power, LogIn, Radio } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Turno() {
  const { user } = useAuth();
  const [shift, setShift] = useState(null);
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/shifts/current");
      setShift((prev) => {
        // Se il backend ha auto-chiuso il turno, avvisa l'utente
        if (prev && !data) {
          toast.warning("Turno chiuso automaticamente: nessuna fattura registrata da oltre 1 ora.", { duration: 8000 });
        }
        return data;
      });
    } catch (e) { console.warn("Errore caricamento turno corrente:", e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Polling ogni 30s per rilevare auto-chiusura del turno lato server
  useEffect(() => {
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (!shift) { setElapsed(0); return; }
    const start = new Date(shift.start_at).getTime();
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [shift]);

  const start = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/shifts/start");
      setShift(data);
      toast.success("Turno avviato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const stop = async () => {
    if (!window.confirm("Confermi la chiusura del turno?")) return;
    setLoading(true);
    try {
      const { data } = await api.post("/shifts/stop");
      toast.success(`Turno terminato · ${formatDuration(data.duration_seconds)}`);
      setShift(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const active = !!shift;
  const initials = (user?.display_name || user?.username || "?").split(/[\s._-]+/).map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="w-full">
      <PageHeader
        icon={Timer}
        title="Turno di Servizio"
        subtitle="Gestisci il tuo turno operativo."
        right={
          <div className={`ot-label flex items-center gap-2 ${active ? "text-emerald-400" : "text-slate-500"}`}>
            <span className={`w-2 h-2 rounded-full ${active ? "bg-emerald-400 ot-blink" : "bg-slate-500"}`} />
            {active ? "IN SERVIZIO" : "OFF-LINE"}
          </div>
        }
      />

      {/* Operator card */}
      <div className="ot-panel p-5 flex items-center justify-between mb-6 ot-fade-up">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-md border border-blue-400/40 bg-blue-400/10 flex items-center justify-center ot-heading font-bold text-blue-400 text-lg">
            {initials}
          </div>
          <div>
            <div className="ot-heading text-xl font-bold text-slate-100 uppercase">{user?.display_name || user?.username}</div>
            <div className="ot-mono text-[0.7rem] text-blue-400/80 tracking-widest uppercase">{user?.role} · OPERATORE LOGGATO</div>
          </div>
        </div>
        <div className={`ot-mono text-sm tracking-widest ${active ? "text-emerald-400" : "text-slate-500"}`}>
          {active ? "ON-LINE" : "OFF-LINE"}
        </div>
      </div>

      {/* Timer section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 ot-fade-up" style={{ animationDelay: "100ms" }}>
        <div className="ot-panel p-8 lg:col-span-2 relative overflow-hidden">
          <div className="ot-label mb-4">// TIMER DI SESSIONE</div>
          <div className="flex flex-col items-center justify-center py-6">
            <div
              className="ot-timer text-6xl sm:text-7xl md:text-8xl mb-2"
              data-testid="shift-timer"
            >
              {formatDuration(elapsed)}
            </div>
            <div className="ot-mono text-[0.7rem] text-slate-500 tracking-widest mt-2">
              {active ? `INIZIO SESSIONE · ${formatDate(shift.start_at)}` : "PRONTO PER INIZIARE"}
            </div>
          </div>
          {/* Progress bar */}
          <div className="mt-4 h-1 bg-white/5 rounded overflow-hidden">
            {active && <div className="h-full bg-gradient-to-r from-blue-400 to-blue-500 ot-blink" style={{ width: "100%" }} />}
          </div>
        </div>

        <div className="ot-panel p-6 flex flex-col justify-between">
          <div>
            <div className="ot-label mb-3">CONTROLLO SESSIONE</div>
            <div className="ot-heading text-2xl font-bold text-slate-100 mb-2">
              {active ? "Termina il turno?" : "Pronto per iniziare?"}
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              {active
                ? "Chiudi la sessione al termine del tuo servizio. Il tempo verrà salvato in cronologia."
                : "Prima di entrare in servizio ricorda di connetterti alla frequenza radio e di indossare la divisa."}
            </p>
          </div>

          <div className="mt-6">
            {!active ? (
              <button
                data-testid="start-shift-btn"
                onClick={start}
                disabled={loading}
                className="ot-btn-primary w-full flex items-center justify-center gap-2"
              >
                <LogIn className="w-4 h-4" />
                Avvia Turno
              </button>
            ) : (
              <button
                data-testid="stop-shift-btn"
                onClick={stop}
                disabled={loading}
                className="ot-btn-danger w-full flex items-center justify-center gap-2"
              >
                <Power className="w-4 h-4" />
                Termina Turno
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="mt-8 flex items-center gap-6 text-slate-500 text-xs ot-mono tracking-widest">
        <span className="flex items-center gap-2"><Radio className="w-3 h-3 text-blue-400" /> CANALE CIFRATO</span>
      </div>
    </div>
  );
}
